import React, { useState, useEffect, useRef } from 'react';
import { db, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, query, where, getDocs, limit } from 'firebase/firestore';
import { nanoid } from 'nanoid';
import swagLibrary from '../data/swagLibrary';
import { allBrands, getVariedBrandSample } from '../data/brandSamples';
import activitiesLibrary from '../data/activitiesLibrary';
import { activityEmojis as allActivityEmojis } from '../data/activityEmojis';
import { searchActivityPlusEmojisByQuery } from '../utils/activityEmojiKeywordSearch';
import { allPlaces, getVariedPlacesSample } from '../data/placesSamples';
import { fetchJungianAnalysis } from '../utils/jungianPrompt';
import { ensureBrowserDisplayablePhotoFile, isLikelyPhotoFile } from '../utils/ensureBrowserDisplayablePhotoFile';
import { generatePartnerCutoutNanoBanana, editPartnerCutoutNanoBanana, applyUserPhotoFoxMask, searchSpiritGifs as searchSpiritGifsApi, searchBrandImages, searchPlaces as searchPlacesApi } from '../services/firebaseApiService';
import { getRandomWord } from '../data/wordLibrary';
import { MOOD_LOCAL_GIFS as LOCAL_GIFS } from '../data/localGifLibraries';
import theFoxMini from '../TheFoxMini.png';
import foxMaskImage from '../foxmask.png';
import scissorsImage from '../scissors.webp';
import heartOutline from '../heart-outline.svg';
import './InlineSurvey.css';

const DEFAULT_VISUALS_HINT = 'What visuals give your vibe?';

const InlineSurvey = ({
  onClose,
  onComplete,
  currentUser,
  onIntroChange,
  skipSurveyIntro = false,
  visualsHintText = DEFAULT_VISUALS_HINT,
  /** Collage-a-friend flow: first question back exits to Your collages → Your rewards (parent handles UI). */
  onFriendFlowExitToRewards,
  /** Friend flow: brands / activities / places subtitles use "their" instead of "your". */
  collageFriendFlow = false,
}) => {
  const [showIntro, setShowIntroState] = useState(() => !skipSurveyIntro);
  const setShowIntro = (val) => {
    setShowIntroState(val);
    if (onIntroChange) onIntroChange(val);
  };

  useEffect(() => {
    if (!skipSurveyIntro) return;
    setShowIntroState(false);
    if (onIntroChange) onIntroChange(false);
  }, [skipSurveyIntro, onIntroChange]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [navigatedBack, setNavigatedBack] = useState(false);
  const [answers, setAnswers] = useState({});
  const [showThankYou, setShowThankYou] = useState(false);
  const [showCutouts, setShowCutouts] = useState(false);
  const [cutouts, setCutouts] = useState([]);
  const [loadingCutouts, setLoadingCutouts] = useState(false);
  const [selectedCutout, setSelectedCutout] = useState(null);
  const [showSwagSelection, setShowSwagSelection] = useState(false);
  const [swags, setSwags] = useState([]);
  const [selectedSwags, setSelectedSwags] = useState([]);
  const [showWordsSelection, setShowWordsSelection] = useState(false);
  const [selectedWords, setSelectedWords] = useState([]);
  const [showActivitiesSelection, setShowActivitiesSelection] = useState(false);
  const [showActivitiesTiles, setShowActivitiesTiles] = useState(false);
  const [partnerActivities, setPartnerActivities] = useState([]);
  const [selectedPartnerActivities, setSelectedPartnerActivities] = useState([]);
  const [selectedMusic, setSelectedMusic] = useState([]);
  const [selectedMovies, setSelectedMovies] = useState([]);
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [selectedPlaces, setSelectedPlaces] = useState([]);
  const [spiritGifs, setSpiritGifs] = useState(() => {
    const shuffled = [...LOCAL_GIFS].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 9);
  });
  const [selectedSpiritGif, setSelectedSpiritGif] = useState(null);
  const [loadingGifs, setLoadingGifs] = useState(false);
  const [gifSearchQuery, setGifSearchQuery] = useState('');
  /** Set after a search that returns nothing or fails (previous grid kept). */
  const [spiritGifFetchHint, setSpiritGifFetchHint] = useState(null);
  const getRandomPlaces = () => getVariedPlacesSample(9);
  const [randomPlaces, setRandomPlaces] = useState(getRandomPlaces);
  const [placeMoreClickCount, setPlaceMoreClickCount] = useState(0);
  const [showPlaceSearch, setShowPlaceSearch] = useState(false);
  const [placeSearchQuery, setPlaceSearchQuery] = useState('');
  const [placeSearchResults, setPlaceSearchResults] = useState([]);
  const [placeSearchLoading, setPlaceSearchLoading] = useState(false);
  const [placeSearchDone, setPlaceSearchDone] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const getRandomBrands = () => getVariedBrandSample(9);
  const [randomBrands, setRandomBrands] = useState(getRandomBrands);
  const [brandMoreClickCount, setBrandMoreClickCount] = useState(0);
  const [showBrandSearch, setShowBrandSearch] = useState(false);
  const [brandSearchQuery, setBrandSearchQuery] = useState('');
  const [brandSearchResults, setBrandSearchResults] = useState([]);
  const [brandSearchLoading, setBrandSearchLoading] = useState(false);
  const [fetchedActivities, setFetchedActivities] = useState([]); // Array of activity objects from Firestore
  const [loadingActivityImages, setLoadingActivityImages] = useState(false);
  const [generatedCutoutUrl, setGeneratedCutoutUrl] = useState(null);
  const generatedCutoutUrlRef = useRef(null);
  const jungianAnalysisRef = useRef(null);
  const brandSearchWarmupRef = useRef(false);
  const placeSearchWarmupRef = useRef(false);
  const spiritGifSearchWarmupRef = useRef(false);
  const generationPromiseRef = useRef(null);
  const analysisPromiseRef = useRef(null);
  const descriptorPromiseRef = useRef(null);
  const [currentInput, setCurrentInput] = useState('');
  const [showCustomInput, setShowCustomInput] = useState({});
  const [customInputValue, setCustomInputValue] = useState('');
  const [editingCustomValue, setEditingCustomValue] = useState(null);
  const [showEmojiDropdown, setShowEmojiDropdown] = useState(false);
  const [activityEmojiMoreClickCount, setActivityEmojiMoreClickCount] = useState(0);
  const [showActivityEmojiSearch, setShowActivityEmojiSearch] = useState(false);
  const [activityEmojiSearchQuery, setActivityEmojiSearchQuery] = useState('');
  const [activityEmojiSearchResults, setActivityEmojiSearchResults] = useState([]);
  const [activityEmojiSearchDone, setActivityEmojiSearchDone] = useState(false);
  const [showOtherInputFor, setShowOtherInputFor] = useState(null); // question id when "Other" was chosen
  const [otherInputValue, setOtherInputValue] = useState('');
  const [isSubmittingOther, setIsSubmittingOther] = useState(false);

  // User photo upload state (survey inline)
  const [surveyUserPhotoFile, setSurveyUserPhotoFile] = useState(null);
  const [surveyUserPhotoPreview, setSurveyUserPhotoPreview] = useState(null);
  const [surveyUserPhotoUploading, setSurveyUserPhotoUploading] = useState(false);
  const [surveyUserPhotoProcessing, setSurveyUserPhotoProcessing] = useState(false);
  const [surveyUserPhotoEditedUrl, setSurveyUserPhotoEditedUrl] = useState(null);
  const [surveyUserPhotoHintIndex, setSurveyUserPhotoHintIndex] = useState(0);
  const surveyUserPhotoInputRef = useRef(null);
  /** Pre–fox-mask Firebase Storage URL; paired with masked answer for unmask / primary sync. */
  const surveyUserPhotoOriginalUrlRef = useRef(null);
  const FOXMASK_IMAGE_URL = 'https://firebasestorage.googleapis.com/v0/b/luvf-634c7.firebasestorage.app/o/cutouts%2Fmale%2Ffoxmask2.png?alt=media&token=a236a08e-e331-42d7-9bed-ef549ff6c61a';
  const PHOTO_HINT_MESSAGES = [
    'Sharpening scissors (5)', 'Sharpening scissors (4)', 'Sharpening scissors (3)', 'Sharpening scissors (2)', 'Sharpening scissors (1)',
    'Adding fox mask.. (4)', 'Adding fox mask.. (3)', 'Adding fox mask.. (2)', 'Adding fox mask.. (1)',
    'Adding fox mask...',
  ];

  // Quotes about personality and looks for "Pick their words"
  const personalityQuotes = [
    "confident",
    "thoughtful",
    "adventurous",
    "mysterious",
    "warm",
    "bold",
    "creative",
    "intelligent",
    "charming",
    "genuine",
    "witty",
    "elegant"
  ];

  // Get 6 random quotes
  const getRandomQuotes = () => {
    const shuffled = [...personalityQuotes].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 6);
  };

  const [availableQuotes, setAvailableQuotes] = useState(getRandomQuotes());
  
  // All available musicians
  const allMusicians = [
    'Taylor Swift', 'Olivia Rodrigo', 'SZA', 'Drake', 'Bad Bunny', 'The Weeknd', 'Doja Cat',
    'Travis Scott', 'NewJeans', 'LE SSERAFIM', 'Stray Kids', 'Seventeen', 'SOBO',
    'Berkeley Nu Jazz Collective', 'UC Berkeley Symphony Orchestra', 'Billie Eilish',
    'Kendrick Lamar', 'Frank Ocean', 'Lana Del Rey', 'Phoebe Bridgers', 'boygenius',
    'Tyler, the Creator', 'Ice Spice', 'Peso Pluma', 'Kali Uchis', 'Steve Lacy',
    'Brent Faiyaz', 'Fred again..', 'Mitski', 'Gracie Abrams', 'The Red Pears',
    'Peach Tree Rascals', 'Dayglow', 'Vacations', 'Surf Curse', 'Calvin Harris',
    'David Guetta', 'Martin Garrix', 'Zedd', 'Marshmello', 'Kygo', 'Illenium',
    'Seven Lions', 'Alesso', 'Peggy Gou', 'Fisher', 'John Summit', 'Dom Dolla',
    'Lane 8', 'ODESZA', 'Disclosure', 'Flume', 'G Jones', 'KSHMR', 'The Glitch Mob', 'DJ Dials'
  ];
  
  // Generate 9 random musicians on component mount
  const getRandomMusicians = () => {
    const shuffled = [...allMusicians].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 9);
  };
  const [randomMusicians, setRandomMusicians] = useState(getRandomMusicians);
  
  // All available movies
  const allMovies = [
    'Wicked', 'Dune: Part Two', 'Inside Out 2', 'Gladiator II', 'Moana 2', 'Deadpool & Wolverine',
    'A Quiet Place: Day One', 'All We Imagine as Light', 'Wallace & Gromit: Vengeance Most Fowl',
    'Anora', 'Nosferatu', 'Ne Zha 2', 'A Minecraft Movie', 'Lilo & Stitch', 'Zootopia 2',
    'Avatar: Fire and Ash', 'Jurassic World: Rebirth', 'Superman', 'Sinners', 'F1: The Movie',
    'The Fantastic Four: First Steps', 'Materialists', 'One Battle After Another', 'Bugonia',
    'Sorry Baby', 'Frankenstein', 'Adolescence', 'Severance', 'The Pitt', 'The Studio',
    'Pluribus', 'The Great British Baking Show', 'Chef\'s Table', 'The Rehearsal', 'Andor',
    'Clean Slate', 'Paradise', 'Forever', 'Loot', 'Supacell', 'Stranger Things',
    'House of the Dragon', 'The Boys', 'Euphoria', 'Squid Game', 'The Last of Us',
    'One Piece', 'Arcane', 'The Bear', 'Shōgun', 'Fallout', 'Wednesday',
    'Oppenheimer', 'Barbie', 'Spider‑Man', 'Joker', 'The Batman',
    'Kingdom of the Planet of the Apes', 'Venom', 'Avengers', 'Thor', 'Hulk', 'Shrek'
  ];
  
  // Generate 9 random movies on component mount
  const getRandomMovies = () => {
    const shuffled = [...allMovies].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 9);
  };
  const [randomMovies, setRandomMovies] = useState(getRandomMovies);
  
  // Religion/spirituality emojis to exclude from random selection (only show in dropdown)
  const religionEmojis = ['🛐', '🧘', '⚛️', '🕉️', '✡️', '☸️', '☯️', '✝️', '☦️', '☪️', '☮️', '🕎', '🔯', '🪯'];
  
  // Generate 9 random activity emojis on component mount (excluding religion/spirituality emojis)
  const [activityEmojis, setActivityEmojis] = useState(() => {
    const filteredEmojis = allActivityEmojis.filter((emoji) => !religionEmojis.includes(emoji));
    const shuffled = [...filteredEmojis].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 9);
  });

  // Close emoji dropdown and "Other" input when question changes; scroll to top of page
  useEffect(() => {
    setShowEmojiDropdown(false);
    setShowOtherInputFor(null);
    setOtherInputValue('');
    setIsSubmittingOther(false);
    setActivityEmojiMoreClickCount(0);
    setShowActivityEmojiSearch(false);
    setActivityEmojiSearchQuery('');
    setActivityEmojiSearchResults([]);
    setActivityEmojiSearchDone(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentQuestion]);

  // Advance hint messages during survey user photo upload/processing
  useEffect(() => {
    if (!surveyUserPhotoUploading && !surveyUserPhotoProcessing) {
      setSurveyUserPhotoHintIndex(0);
      return;
    }
    let idx = 0;
    const tick = () => {
      idx += 1;
      setSurveyUserPhotoHintIndex(Math.min(idx, PHOTO_HINT_MESSAGES.length - 1));
      const delay =
        idx < 5 ? 1000 : idx === 5 ? 1500 : idx < 9 ? 1000 : 1500;
      timerId = setTimeout(tick, delay);
    };
    let timerId = setTimeout(tick, 1000);
    return () => clearTimeout(timerId);
  }, [surveyUserPhotoUploading, surveyUserPhotoProcessing]);

  // Warm searchBrandImages (Firebase + Brave) cold start when the brands step is visible
  useEffect(() => {
    if (showIntro || currentQuestion !== 0) return;
    if (brandSearchWarmupRef.current) return;
    brandSearchWarmupRef.current = true;
    searchBrandImages('warmup', 1).catch(() => {});
  }, [showIntro, currentQuestion]);

  // Warm searchPlaces (Firebase + Google Places) when the places step is visible (questions[2])
  useEffect(() => {
    if (showIntro || currentQuestion !== 2) return;
    if (placeSearchWarmupRef.current) return;
    placeSearchWarmupRef.current = true;
    searchPlacesApi('warmup').catch(() => {});
  }, [showIntro, currentQuestion]);

  // Warm searchSpiritGifs (Firebase + KLIPY) when the mood GIF step is visible (questions[3])
  useEffect(() => {
    if (showIntro || currentQuestion !== 3) return;
    if (spiritGifSearchWarmupRef.current) return;
    spiritGifSearchWarmupRef.current = true;
    const customerId = currentUser?.uid || 'anonymous';
    searchSpiritGifsApi('warmup', customerId).catch(() => {});
  }, [showIntro, currentQuestion, currentUser?.uid]);

  const pickThreeTheir = collageFriendFlow ? 'their' : 'your';
  const questions = [
    {
      id: 'brands',
      question: "Pick Three",
      subtitle: `${pickThreeTheir} stuff`,
      type: 'brands',
      maxSelections: 3,
      options: randomBrands
    },
    {
      id: 'activities',
      question: "Pick Three",
      subtitle: `${pickThreeTheir} emojis`,
      type: 'multiple',
      maxSelections: 3,
      options: activityEmojis
    },
    {
      id: 'places',
      question: "Pick Three",
      subtitle: `${pickThreeTheir} places`,
      type: 'places',
      maxSelections: 3,
      options: randomPlaces
    },
    {
      id: 'spirit_gif',
      question: "AURA GIF",
      type: 'spirit_gif',
      maxSelections: 1
    },
    {
      id: 'user_photo',
      question: collageFriendFlow ? "UPLOAD YOUR FRIEND'S PHOTO" : "UPLOAD YOUR PHOTO",
      subtitle: collageFriendFlow
        ? "We'll cover their face with a fox mask for privacy"
        : "We'll mask your face with a fox for privacy",
      type: 'user_photo',
      maxSelections: 1
    },
  ];

  const fetchCutouts = async (userAnswers) => {
    setLoadingCutouts(true);
    try {
      // Build query based on user responses
      // We'll match cutouts based on gender preference and interests
      const cutoutsRef = collection(db, 'partner_cutouts');
      
      // Create a query based on looking_for (gender preference)
      const lookingFor = userAnswers.looking_for;
      let cutoutsQuery;
      
      // Normalize gender value for matching
      const normalizeGender = (gender) => {
        if (!gender) return null;
        const normalized = gender.toLowerCase().trim();
        // Map common variations to standard values
        if (normalized === 'male' || normalized === 'man') return 'Male';
        if (normalized === 'female' || normalized === 'woman') return 'Female';
        if (normalized === 'nb' || normalized === 'non-binary' || normalized === 'nonbinary') return 'NB';
        return gender; // Return original if no match
      };
      
      const normalizedLookingFor = normalizeGender(lookingFor);
      
      if (normalizedLookingFor) {
        // Match cutouts that have tags matching the user's preferences
        cutoutsQuery = query(
          cutoutsRef,
          where('gender', '==', normalizedLookingFor),
          limit(20) // Get more than 4 to have options
        );
      } else {
        // If no gender preference, just get any cutouts
        cutoutsQuery = query(cutoutsRef, limit(20));
      }
      
      const querySnapshot = await getDocs(cutoutsQuery);
      const fetchedCutouts = [];
      
      querySnapshot.forEach((doc) => {
        fetchedCutouts.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      // If we have fewer than 4 cutouts, fetch more without gender filter
      if (fetchedCutouts.length < 4) {
        const fallbackQuery = query(cutoutsRef, limit(20));
        const fallbackSnapshot = await getDocs(fallbackQuery);
        fallbackSnapshot.forEach((doc) => {
          if (!fetchedCutouts.find(c => c.id === doc.id)) {
            fetchedCutouts.push({
              id: doc.id,
              ...doc.data()
            });
          }
        });
      }
      
      // Score and sort cutouts based on user interests
      const scoredCutouts = fetchedCutouts.map(cutout => {
        let score = 0;
        const userInterests = [
          ...(userAnswers.music || []),
          ...(userAnswers.movies || []),
          ...(userAnswers.activities || [])
        ];
        
        // Match based on tags if available
        if (cutout.tags && Array.isArray(cutout.tags)) {
          userInterests.forEach(interest => {
            if (cutout.tags.some(tag => 
              tag.toLowerCase().includes(interest.toLowerCase()) ||
              interest.toLowerCase().includes(tag.toLowerCase())
            )) {
              score += 1;
            }
          });
        }
        
        return { ...cutout, score };
      });
      
      // Sort by score (highest first) and take top 4
      const topCutouts = scoredCutouts
        .sort((a, b) => b.score - a.score)
        .slice(0, 4);
      
      // If we still don't have 4, fill with random ones
      if (topCutouts.length < 4) {
        const remaining = fetchedCutouts
          .filter(c => !topCutouts.find(tc => tc.id === c.id))
          .slice(0, 4 - topCutouts.length);
        topCutouts.push(...remaining);
      }
      
      // Shuffle to avoid always showing the same order
      const shuffled = topCutouts.sort(() => Math.random() - 0.5);
      setCutouts(shuffled.slice(0, 4));
    } catch (error) {
      console.error('Error fetching cutouts:', error);
      // On error, set empty array - UI will handle gracefully
      setCutouts([]);
    } finally {
      setLoadingCutouts(false);
    }
  };

  const loadSwags = async () => {
    try {
      // Fetch swags from Firestore
      const swagsRef = collection(db, 'swags');
      const swagsQuery = query(swagsRef, limit(20)); // Get up to 20 swags
      const querySnapshot = await getDocs(swagsQuery);
      
      const fetchedSwags = [];
      querySnapshot.forEach((doc) => {
        const swagData = doc.data();
        fetchedSwags.push({
          id: swagData.id || doc.id,
          imageUrl: swagData.imageUrl,
          name: swagData.name || swagData.tags || `Swag ${doc.id}`,
          tags: swagData.tags
        });
      });
      
      // If we have swags from Firestore, use them
      if (fetchedSwags.length > 0) {
        // Take first 6 or all available
        setSwags(fetchedSwags.slice(0, 6));
      } else {
        // Fallback to local library if Firestore is empty
        const availableSwags = swagLibrary.length > 0 
          ? swagLibrary.slice(0, 6)
          : [];
        
        // If both are empty, create placeholder swags
        if (availableSwags.length === 0) {
          const placeholderSwags = Array.from({ length: 6 }, (_, index) => ({
            id: `swag-placeholder-${index + 1}`,
            imageUrl: null,
            name: `Swag ${index + 1}`
          }));
          setSwags(placeholderSwags);
        } else {
          setSwags(availableSwags);
        }
      }
    } catch (error) {
      console.error('Error fetching swags:', error);
      // Fallback to local library on error
      const availableSwags = swagLibrary.length > 0 
        ? swagLibrary.slice(0, 6)
        : [];
      
      if (availableSwags.length === 0) {
        const placeholderSwags = Array.from({ length: 6 }, (_, index) => ({
          id: `swag-placeholder-${index + 1}`,
          imageUrl: null,
          name: `Swag ${index + 1}`
        }));
        setSwags(placeholderSwags);
      } else {
        setSwags(availableSwags);
      }
    }
  };

  const loadActivities = () => {
    // Load activities from library - take first 6 or all available
    const availableActivities = activitiesLibrary.length > 0 
      ? activitiesLibrary.slice(0, 6)
      : [];
    
    // If library is empty, create placeholder activities for now
    if (availableActivities.length === 0) {
      const placeholderActivities = Array.from({ length: 6 }, (_, index) => ({
        id: `activity-placeholder-${index + 1}`,
        imageUrl: null,
        name: `Activity ${index + 1}`
      }));
      setPartnerActivities(placeholderActivities);
    } else {
      setPartnerActivities(availableActivities);
    }
  };

  const loadActivityImages = async () => {
    setLoadingActivityImages(true);
    try {
      // Fetch activities from Firestore
      const activitiesRef = collection(db, 'activities');
      const activitiesQuery = query(activitiesRef, limit(50)); // Get up to 50 activities to choose from
      const querySnapshot = await getDocs(activitiesQuery);
      
      const allActivities = [];
      querySnapshot.forEach((doc) => {
        const activityData = doc.data();
        allActivities.push({
          id: activityData.id || doc.id,
          name: activityData.name || activityData.id || `Activity ${doc.id}`,
          imageUrl: activityData.imageUrl || null
        });
      });
      
      // Shuffle and take 12 random activities
      const shuffled = [...allActivities].sort(() => Math.random() - 0.5);
      const selected = shuffled.slice(0, 12);
      
      // Fill remaining slots with placeholders if we have fewer than 12
      while (selected.length < 12) {
        selected.push({
          id: `placeholder-${selected.length + 1}`,
          name: `Activity ${selected.length + 1}`,
          imageUrl: null
        });
      }
      
      setFetchedActivities(selected);
    } catch (error) {
      console.error('Error fetching activity images:', error);
      // Create placeholder activities if fetch fails
      const placeholders = Array.from({ length: 12 }, (_, index) => ({
        id: `placeholder-${index + 1}`,
        name: `Activity ${index + 1}`,
        imageUrl: null
      }));
      setFetchedActivities(placeholders);
    } finally {
      setLoadingActivityImages(false);
    }
  };

  // User photo upload handlers (survey inline)
  const handleSurveyUserPhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isLikelyPhotoFile(file)) {
      alert('Please upload an image file');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      alert('Image size must be less than 15MB');
      return;
    }
    void (async () => {
      try {
        const displayFile = await ensureBrowserDisplayablePhotoFile(file);
        setSurveyUserPhotoFile(displayFile);
        const reader = new FileReader();
        reader.onloadend = () => setSurveyUserPhotoPreview(reader.result);
        reader.readAsDataURL(displayFile);
      } catch (err) {
        console.error('HEIF conversion failed:', err);
        alert('Couldn’t read this HEIF photo. Try exporting it as JPEG.');
      }
    })();
  };

  const removeSurveyUserPhoto = () => {
    setSurveyUserPhotoFile(null);
    setSurveyUserPhotoPreview(null);
    setSurveyUserPhotoEditedUrl(null);
    surveyUserPhotoOriginalUrlRef.current = null;
    if (surveyUserPhotoInputRef.current) surveyUserPhotoInputRef.current.value = '';
  };

  const handleSurveyUserPhotoSubmit = async () => {
    if (!surveyUserPhotoFile) return;
    setSurveyUserPhotoHintIndex(0);
    setSurveyUserPhotoUploading(true);
    try {
      const storagePath = `user_photos/${Date.now()}_${nanoid(8)}/${surveyUserPhotoFile.name}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, surveyUserPhotoFile, {
        contentType: surveyUserPhotoFile.type || 'image/jpeg',
      });
      const downloadUrl = await getDownloadURL(storageRef);
      surveyUserPhotoOriginalUrlRef.current = downloadUrl;
      setSurveyUserPhotoUploading(false);
      setSurveyUserPhotoProcessing(true);
      try {
        const editedUrl = await applyUserPhotoFoxMask(downloadUrl);
        setSurveyUserPhotoEditedUrl(editedUrl);
      } catch (err) {
        console.error('User photo fox mask edit failed:', err);
        alert('Failed to process photo. Please try again.');
      } finally {
        setSurveyUserPhotoProcessing(false);
      }
    } catch (err) {
      console.error('User photo upload failed:', err);
      alert('Failed to upload photo. Please try again.');
      setSurveyUserPhotoUploading(false);
    }
  };

  const confirmSurveyUserPhoto = async () => {
    if (!surveyUserPhotoEditedUrl) return;
    let urlToSave = surveyUserPhotoEditedUrl;
    if (typeof urlToSave === 'string' && urlToSave.includes('replicate.delivery')) {
      try {
        const res = await fetch(urlToSave);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const ext = (blob.type && blob.type.split('/')[1]) || 'png';
        const storagePath = `user_photos/${Date.now()}_${nanoid(8)}.${ext}`;
        const storageRefPersist = ref(storage, storagePath);
        await uploadBytes(storageRefPersist, blob, { contentType: blob.type || 'image/png' });
        urlToSave = await getDownloadURL(storageRefPersist);
      } catch (e) {
        console.warn('Could not persist user photo to Storage:', e);
      }
    }
    handleAnswer({
      masked: urlToSave,
      original: surveyUserPhotoOriginalUrlRef.current || null,
    });
  };

  const fetchSpiritGifs = async (searchQuery = 'mood') => {
    setLoadingGifs(true);
    setSpiritGifFetchHint(null);
    try {
      const customerId = currentUser?.uid || `anon_${Date.now()}`;
      const gifs = await searchSpiritGifsApi(searchQuery, customerId);
      const next = (gifs || []).slice(0, 9);
      if (next.length === 0) {
        setSpiritGifFetchHint('no_results');
      } else {
        setSpiritGifs(next);
      }
    } catch (error) {
      console.error('Error fetching spirit GIFs:', error);
      setSpiritGifFetchHint('error');
    } finally {
      setLoadingGifs(false);
    }
  };

  // Auto-select cutout, swags, and words based on user responses
  const autoSelectPartnerComponents = async (userAnswers) => {
    try {
      const selectedCutout = null;
      
      // Get user interests for swags selection
      const userInterests = [
        ...(userAnswers.music || []),
        ...(userAnswers.movies || []),
        ...(userAnswers.books || []),
        ...(userAnswers.activities || [])
      ];
      
      // Select swags
      const swagsRef = collection(db, 'swags');
      const swagsQuery = query(swagsRef, limit(20));
      const swagsSnapshot = await getDocs(swagsQuery);
      
      const fetchedSwags = [];
      swagsSnapshot.forEach((doc) => {
        const swagData = doc.data();
        fetchedSwags.push({
          id: swagData.id || doc.id,
          imageUrl: swagData.imageUrl,
          name: swagData.name || swagData.tags || `Swag ${doc.id}`,
          tags: swagData.tags
        });
      });
      
      // Score swags based on user interests
      const scoredSwags = fetchedSwags.map(swag => {
        let score = 0;
        if (swag.tags && Array.isArray(swag.tags)) {
          userInterests.forEach(interest => {
            if (swag.tags.some(tag => 
              tag.toLowerCase().includes(interest.toLowerCase()) ||
              interest.toLowerCase().includes(tag.toLowerCase())
            )) {
              score += 1;
            }
          });
        }
        return { ...swag, score };
      });
      
      const sortedSwags = scoredSwags.sort((a, b) => b.score - a.score);
      const selectedSwags = sortedSwags.slice(0, 3); // Pick top 3
      
      // If we don't have enough, fill with random ones
      if (selectedSwags.length < 3 && fetchedSwags.length > selectedSwags.length) {
        const remaining = fetchedSwags
          .filter(s => !selectedSwags.find(ss => ss.id === s.id))
          .slice(0, 3 - selectedSwags.length);
        selectedSwags.push(...remaining);
      }
      
      // Select words (pick 3 random quotes)
      const shuffledQuotes = [...personalityQuotes].sort(() => Math.random() - 0.5);
      const selectedWords = shuffledQuotes.slice(0, 3);
      
      return {
        cutout: selectedCutout,
        swags: selectedSwags,
        words: selectedWords
      };
    } catch (error) {
      console.error('Error auto-selecting partner components:', error);
      // Return nulls on error - will be handled by caller
      return {
        cutout: null,
        swags: [],
        words: []
      };
    }
  };

  // Start partner cutout generation after user answers movies question (runs in background; image shown only after survey complete).
  // analysisPromise resolves when analysis is ready (so parent can show nickname before image); generationPromise resolves when image is ready.
  const startPartnerCutoutGeneration = (newAnswers) => {
    const userTier = currentUser?.tier || 'free';
    const lookingFor = newAnswers?.looking_for ?? '';
    const analysisPromise = fetchJungianAnalysis(newAnswers, userTier).then((analysis) => {
      if (analysis) jungianAnalysisRef.current = analysis;
      return analysis;
    });
    analysisPromiseRef.current = analysisPromise;
    const fullPromise = analysisPromise
      .then(async (analysis) => {
        let imageUrl = null;
        try {
          imageUrl = await generatePartnerCutoutNanoBanana(analysis || '', userTier, lookingFor);
        } catch (err) {
          console.warn('Partner cutout image generation failed:', err?.message);
        }
        return { analysis: analysis || jungianAnalysisRef.current, imageUrl };
      })
      .catch((err) => {
        console.error('Background partner cutout generation failed:', err);
        return { analysis: jungianAnalysisRef.current, imageUrl: null };
      });
    generationPromiseRef.current = fullPromise;
    fullPromise.then((result) => {
      if (result?.imageUrl) {
        setGeneratedCutoutUrl(result.imageUrl);
        generatedCutoutUrlRef.current = result.imageUrl;
      }
      if (result?.analysis) jungianAnalysisRef.current = result.analysis;
    });
  };

  const completeSurvey = async (newAnswers) => {
    try {
      // Save survey response (associate with user if logged in; link on sign-up if anonymous)
      const userId = currentUser?.uid ?? 'anonymous';
      const docRef = await addDoc(collection(db, 'survey_responses'), {
        ...newAnswers,
        timestamp: new Date(),
        userId,
      });
      if (userId === 'anonymous') {
        sessionStorage.setItem('lastSurveyResponseId', docRef.id);
      }

      // Auto-select cutout, swags, and words based on user responses
      const selectedComponents = await autoSelectPartnerComponents(newAnswers);

      // Complete the survey with auto-selected components (always call onComplete to mark survey as complete)
      if (onComplete) {
        onComplete({
          answers: newAnswers,
          cutout: selectedComponents.cutout,
          swags: selectedComponents.swags || [],
          words: selectedComponents.words || [],
          activities: newAnswers.activities || [],
          places: newAnswers.places || [],
          generatedCutoutUrl: generatedCutoutUrlRef.current || generatedCutoutUrl || undefined,
          jungianAnalysis: jungianAnalysisRef.current || undefined,
          analysisPromise: analysisPromiseRef.current || undefined,
          generationPromise: generationPromiseRef.current || undefined,
          descriptorPromise: descriptorPromiseRef.current || undefined,
        });
      }

      // Close the survey (parent keeps collage-a-friend flags until save / dismiss)
      onClose({ completed: true });
    } catch (error) {
      console.error('Error saving survey:', error);
      try {
        const selectedComponents = await autoSelectPartnerComponents(newAnswers);
        if (onComplete) {
          onComplete({
            answers: newAnswers,
            cutout: selectedComponents.cutout,
            swags: selectedComponents.swags || [],
            words: selectedComponents.words || [],
            activities: newAnswers.activities || [],
            places: newAnswers.places || [],
            generatedCutoutUrl: generatedCutoutUrlRef.current || generatedCutoutUrl || undefined,
            jungianAnalysis: jungianAnalysisRef.current || undefined,
            analysisPromise: analysisPromiseRef.current || undefined,
            generationPromise: generationPromiseRef.current || undefined,
            descriptorPromise: descriptorPromiseRef.current || undefined,
          });
        }
        onClose({ completed: true });
      } catch (selectError) {
        console.error('Error auto-selecting components:', selectError);
        if (onComplete) {
          onComplete({
            answers: newAnswers,
            cutout: null,
            swags: [],
            words: [],
            activities: newAnswers.activities || [],
            places: newAnswers.places || [],
            generatedCutoutUrl: generatedCutoutUrlRef.current || generatedCutoutUrl || undefined,
            jungianAnalysis: jungianAnalysisRef.current || undefined,
            analysisPromise: analysisPromiseRef.current || undefined,
            generationPromise: generationPromiseRef.current || undefined,
            descriptorPromise: descriptorPromiseRef.current || undefined,
          });
        }
        onClose({ completed: true });
      }
    }
  };

  const handleAnswer = async (answer) => {
    setNavigatedBack(false);
    const qid = questions[currentQuestion].id;
    let newAnswers = { ...answers };
    if (qid === 'user_photo' && answer && typeof answer === 'object' && answer.masked != null) {
      newAnswers.user_photo = answer.masked;
      newAnswers.user_photo_original = answer.original ?? null;
    } else {
      newAnswers[qid] = answer;
      if (qid === 'user_photo' && answer == null) {
        newAnswers.user_photo_original = null;
      }
    }
    if (questions[currentQuestion].id === 'spirit_gif' && (answer?.fullUrl || answer?.gifUrl)) {
      newAnswers.vibe_photo = answer.fullUrl || answer.gifUrl;
    }
    setAnswers(newAnswers);

    if (questions[currentQuestion].id === 'music' && newAnswers.music?.length) {
      descriptorPromiseRef.current = Promise.resolve(getRandomWord());
    }

    if (questions[currentQuestion].id === 'movies' && newAnswers.movies?.length) {
      startPartnerCutoutGeneration(newAnswers);
    }

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      const nextQuestion = questions[currentQuestion + 1];
      if (nextQuestion.id === 'music') {
        setSelectedMusic(newAnswers.music || []);
      } else if (nextQuestion.id === 'movies') {
        setSelectedMovies(newAnswers.movies || []);
      } else if (nextQuestion.id === 'brands') {
        setSelectedBrands(newAnswers.brands || []);
      } else if (nextQuestion.id === 'activities') {
        setSelectedActivities(newAnswers.activities || []);
      } else if (nextQuestion.id === 'places') {
        setSelectedPlaces(newAnswers.places || []);
      } else if (nextQuestion.id === 'spirit_gif') {
        setSelectedSpiritGif(newAnswers.spirit_gif || null);
      } else if (nextQuestion.id === 'user_photo') {
        removeSurveyUserPhoto();
      }
      setCurrentInput('');
      setShowCustomInput({});
      setCustomInputValue('');
    } else {
      await completeSurvey(newAnswers);
    }
  };

  const handleBack = () => {
    // If "Other" input is open, close it and show options again
    if (showOtherInputFor) {
      setShowOtherInputFor(null);
      setOtherInputValue('');
      setIsSubmittingOther(false);
      return;
    }
    if (currentQuestion === 0) {
      if (typeof onFriendFlowExitToRewards === 'function') {
        onFriendFlowExitToRewards();
        return;
      }
      setShowIntro(true);
    } else if (currentQuestion > 0) {
      const prevQuestionIndex = currentQuestion - 1;
      const prevQuestion = questions[prevQuestionIndex];
      
      // If we're on activities question and showing activities tiles, hide tiles first
      if (questions[currentQuestion]?.id === 'activities' && showActivitiesTiles) {
        setShowActivitiesTiles(false);
      }
      
      setCurrentQuestion(prevQuestionIndex);
      setNavigatedBack(true);
      if (prevQuestion.id === 'music') {
        setSelectedMusic(answers.music || []);
      } else if (prevQuestion.id === 'movies') {
        setSelectedMovies(answers.movies || []);
      } else if (prevQuestion.id === 'brands') {
        setSelectedBrands(answers.brands || []);
      } else if (prevQuestion.id === 'activities') {
        setSelectedActivities(answers.activities || []);
      } else if (prevQuestion.id === 'places') {
        setSelectedPlaces(answers.places || []);
      } else if (prevQuestion.id === 'spirit_gif') {
        setSelectedSpiritGif(answers.spirit_gif || null);
      } else if (prevQuestion.id === 'user_photo') {
        removeSurveyUserPhoto();
      }
      setCurrentInput(answers[prevQuestion.id] || '');
      setShowCustomInput({});
      setCustomInputValue('');
      setEditingCustomValue(null);
    }
  };

  const handleDeleteCustomValue = (customValue, e) => {
    e.stopPropagation(); // Prevent triggering the tile click
    const question = questions[currentQuestion];
    
    let currentSelected, setSelected;
    
    if (question.id === 'music') {
      currentSelected = selectedMusic;
      setSelected = setSelectedMusic;
    } else if (question.id === 'movies') {
      currentSelected = selectedMovies;
      setSelected = setSelectedMovies;
    } else if (question.id === 'activities') {
      currentSelected = selectedActivities;
      setSelected = setSelectedActivities;
    } else {
      return;
    }
    
    const newSelected = currentSelected.filter(item => item !== customValue);
    setSelected(newSelected);
  };

  const handleMultipleChoice = (option) => {
    const question = questions[currentQuestion];
    
    // Handle "show new options" for music (regenerate non-selected tiles)
    if (option === 'show new options' && question.id === 'music') {
      const pool = allMusicians.filter((m) => !selectedMusic.includes(m));
      const needCount = 9 - selectedMusic.length;
      const shuffled = [...pool].sort(() => Math.random() - 0.5);
      const newOptions = [...selectedMusic, ...shuffled.slice(0, needCount)];
      setRandomMusicians(newOptions);
      return;
    }

    // Handle "show new options" for movies (regenerate non-selected tiles)
    if (option === 'show new options' && question.id === 'movies') {
      const pool = allMovies.filter((m) => !selectedMovies.includes(m));
      const needCount = 9 - selectedMovies.length;
      const shuffled = [...pool].sort(() => Math.random() - 0.5);
      const newOptions = [...selectedMovies, ...shuffled.slice(0, needCount)];
      setRandomMovies(newOptions);
      return;
    }


    // Handle "+ add yours" special case
    if (option === '+ add yours') {
      if (question.id === 'activities') {
        // For activities, show emoji dropdown instead of text input
        setShowEmojiDropdown(true);
      } else {
        setShowCustomInput({ ...showCustomInput, [question.id]: true });
        setCustomInputValue('');
        setEditingCustomValue(null);
      }
      return;
    }
    
    let currentSelected, setSelected;
    
    if (question.id === 'music') {
      currentSelected = selectedMusic;
      setSelected = setSelectedMusic;
    } else if (question.id === 'movies') {
      currentSelected = selectedMovies;
      setSelected = setSelectedMovies;
    } else if (question.id === 'activities') {
      currentSelected = selectedActivities;
      setSelected = setSelectedActivities;
    } else {
      return;
    }
    
    // Check if this is a custom value (not in predefined options)
    const predefinedOptions = question.options;
    const isCustomValue = !predefinedOptions.includes(option);
    
    // If it's a custom value and it's selected, allow editing (don't toggle selection)
    if (isCustomValue && currentSelected.includes(option)) {
      setEditingCustomValue(option);
      setCustomInputValue(option);
      setShowCustomInput({ ...showCustomInput, [question.id]: true });
      return;
    }
    
    // For predefined options or unselected custom values, toggle selection
    let newSelected;
    if (currentSelected.includes(option)) {
      newSelected = currentSelected.filter(item => item !== option);
      setSelected(newSelected);
    } else {
      newSelected = [...currentSelected, option];
      setSelected(newSelected);
      
      // Auto-advance to next question if maxSelections is reached
      if (question.maxSelections && newSelected.length === question.maxSelections) {
        // Use setTimeout to ensure state is updated before calling handleAnswer
        setTimeout(() => {
          handleAnswer(newSelected);
        }, 100);
      }
    }
  };

  const handleCustomInputSubmit = (e) => {
    if (e.key === 'Enter' && customInputValue.trim()) {
      const question = questions[currentQuestion];
      let currentSelected, setSelected;
      
      if (question.id === 'music') {
        currentSelected = selectedMusic;
        setSelected = setSelectedMusic;
      } else if (question.id === 'movies') {
        currentSelected = selectedMovies;
        setSelected = setSelectedMovies;
      } else if (question.id === 'books') {
        currentSelected = selectedBooks;
        setSelected = setSelectedBooks;
      } else if (question.id === 'activities') {
        currentSelected = selectedActivities;
        setSelected = setSelectedActivities;
      } else {
        return;
      }
      
      const newValue = customInputValue.trim();
      
      // If editing an existing custom value, replace it
      if (editingCustomValue) {
        // Check if the new value already exists (and it's not the one we're editing)
        if (currentSelected.includes(newValue) && newValue !== editingCustomValue) {
          // Value already exists, just remove the one being edited
          setSelected(currentSelected.filter(item => item !== editingCustomValue));
        } else {
          // Replace the old value with the new one
          const updatedSelected = currentSelected.map(item => 
            item === editingCustomValue ? newValue : item
          );
          setSelected(updatedSelected);
        }
        setEditingCustomValue(null);
      } else {
        // Add new custom value only if it doesn't already exist
        if (!currentSelected.includes(newValue)) {
          const newSelected = [...currentSelected, newValue];
          setSelected(newSelected);
          
          // Auto-advance to next question if maxSelections is reached
          if (question.maxSelections && newSelected.length === question.maxSelections) {
            setCustomInputValue('');
            setShowCustomInput({ ...showCustomInput, [question.id]: false });
            // Use setTimeout to ensure state is updated before calling handleAnswer
            setTimeout(() => {
              handleAnswer(newSelected);
            }, 100);
            return;
          }
        }
      }
      
      setCustomInputValue('');
      setShowCustomInput({ ...showCustomInput, [question.id]: false });
    }
  };

  const handleCustomInputBlur = () => {
    if (customInputValue.trim()) {
      const question = questions[currentQuestion];
      let currentSelected, setSelected;
      
      if (question.id === 'music') {
        currentSelected = selectedMusic;
        setSelected = setSelectedMusic;
      } else if (question.id === 'movies') {
        currentSelected = selectedMovies;
        setSelected = setSelectedMovies;
      } else if (question.id === 'books') {
        currentSelected = selectedBooks;
        setSelected = setSelectedBooks;
      } else if (question.id === 'activities') {
        currentSelected = selectedActivities;
        setSelected = setSelectedActivities;
      } else {
        return;
      }
      
      const newValue = customInputValue.trim();
      
      // If editing an existing custom value, replace it
      if (editingCustomValue) {
        // Check if the new value already exists (and it's not the one we're editing)
        if (currentSelected.includes(newValue) && newValue !== editingCustomValue) {
          // Value already exists, just remove the one being edited
          setSelected(currentSelected.filter(item => item !== editingCustomValue));
        } else {
          // Replace the old value with the new one
          const updatedSelected = currentSelected.map(item => 
            item === editingCustomValue ? newValue : item
          );
          setSelected(updatedSelected);
        }
        setEditingCustomValue(null);
      } else {
        // Add new custom value only if it doesn't already exist
        if (!currentSelected.includes(newValue)) {
          const newSelected = [...currentSelected, newValue];
          setSelected(newSelected);
          
          // Auto-advance to next question if maxSelections is reached
          if (question.maxSelections && newSelected.length === question.maxSelections) {
            setCustomInputValue('');
            setShowCustomInput({ ...showCustomInput, [question.id]: false });
            setEditingCustomValue(null);
            // Use setTimeout to ensure state is updated before calling handleAnswer
            setTimeout(() => {
              handleAnswer(newSelected);
            }, 100);
            return;
          }
        }
      }
      
      setCustomInputValue('');
    }
    setShowCustomInput({ ...showCustomInput, [questions[currentQuestion].id]: false });
    setEditingCustomValue(null);
  };

  const handleNext = async () => {
    const question = questions[currentQuestion];
    if (question.type === 'user_photo') {
      if (surveyUserPhotoEditedUrl) {
        confirmSurveyUserPhoto();
      } else {
        handleAnswer(null);
      }
      return;
    }
    if (question.type === 'spirit_gif') {
      if (selectedSpiritGif) handleAnswer(selectedSpiritGif);
      return;
    }
    if (question.type === 'brands') {
      handleAnswer(selectedBrands);
    } else if (question.type === 'places') {
      handleAnswer(selectedPlaces);
    } else if (question.type === 'multiple') {
      if (question.id === 'music') {
        handleAnswer(selectedMusic);
      } else if (question.id === 'movies') {
        handleAnswer(selectedMovies);
      } else if (question.id === 'activities') {
        handleAnswer(selectedActivities);
      }
    } else if (question.type === 'text' && currentInput.trim()) {
      handleAnswer(currentInput.trim());
    } else if (question.type === 'single' && answers[question.id]) {
      // For single choice, the answer is already set when clicked, just move forward
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        // Reset input for next question
        const nextQuestion = questions[currentQuestion + 1];
        if (nextQuestion.type === 'text') {
          setCurrentInput(answers[nextQuestion.id] || '');
        }
      }
    }
  };

  const isNextDisabled = () => {
    const question = questions[currentQuestion];
    if (question.type === 'brands') {
      return selectedBrands.length === 0 || selectedBrands.length > question.maxSelections;
    } else if (question.type === 'places') {
      return selectedPlaces.length === 0 || selectedPlaces.length > question.maxSelections;
    } else if (question.type === 'multiple') {
      let currentSelected;
      if (question.id === 'music') {
        currentSelected = selectedMusic;
      } else if (question.id === 'movies') {
        currentSelected = selectedMovies;
      } else if (question.id === 'activities') {
        currentSelected = selectedActivities;
      } else {
        return true;
      }
      if (question.maxSelections) {
        // Allow proceeding with 1 to maxSelections items (up to maxSelections)
        return currentSelected.length === 0 || currentSelected.length > question.maxSelections;
      }
      return currentSelected.length === 0;
    } else if (question.type === 'text') {
      return !currentInput.trim();
    } else if (question.type === 'single') {
      // For single choice questions, check if an answer has been selected
      return !answers[question.id];
    } else if (question.type === 'spirit_gif') {
      return !selectedSpiritGif;
    } else if (question.type === 'user_photo') {
      return false;
    }
    return false;
  };

  const isFullyAnswered = () => {
    const question = questions[currentQuestion];
    if (!question.maxSelections) return false;
    if (question.type === 'brands') return selectedBrands.length === question.maxSelections;
    if (question.type === 'places') return selectedPlaces.length === question.maxSelections;
    if (question.type === 'spirit_gif') return !!selectedSpiritGif;
    if (question.type === 'multiple') {
      if (question.id === 'music') return selectedMusic.length === question.maxSelections;
      if (question.id === 'movies') return selectedMovies.length === question.maxSelections;
      if (question.id === 'activities') return selectedActivities.length === question.maxSelections;
    }
    return false;
  };

  const isBackDisabled = () => {
    // Back button is always enabled - on first question it closes the survey
    return false;
  };

  // Activities tiles are loaded when showActivitiesTiles becomes true (after swag selection)

  const renderQuestionContent = () => {
    const question = questions[currentQuestion];

    switch (question.type) {
      case 'text':
        return (
          <input
            type="text"
            className="survey-input"
            placeholder={question.placeholder}
            value={currentInput}
            onChange={(e) => setCurrentInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && currentInput.trim()) {
                handleAnswer(currentInput.trim());
              }
            }}
          />
        );

      case 'tiles':
        // Handle tile-based selection (like cutouts) for activities
        if (question.id === 'activities') {
          return (
            <div className="activities-tiles-grid">
              {loadingActivityImages && fetchedActivities.length === 0 ? (
                <div className="cutout-loading" style={{ gridColumn: '1 / -1', padding: '2rem' }}>
                  Loading activity images...
                </div>
              ) : (
                fetchedActivities.map((activity) => {
                  const isSelected = selectedActivities.some(a => a.id === activity.id);
                  const isDisabled = !isSelected && selectedActivities.length >= 3;
                  return (
                    <button
                      key={activity.id}
                      className={`activity-tile ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedActivities(selectedActivities.filter(a => a.id !== activity.id));
                        } else if (selectedActivities.length < 3) {
                          setSelectedActivities([...selectedActivities, activity]);
                        }
                      }}
                      disabled={isDisabled}
                    >
                      {activity.imageUrl ? (
                        <img 
                          src={activity.imageUrl} 
                          alt={activity.name} 
                          className="activity-tile-image"
                        />
                      ) : (
                        <div className="activity-tile-placeholder">
                          <span>{activity.name}</span>
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          );
        }
        return null;

      case 'spirit_gif':
        return (
          <div className="spirit-gif-section">
            {!loadingGifs && spiritGifFetchHint === 'no_results' && (
              <p className="spirit-gif-hint" role="status">
                No GIFs for that search. Try a different keyword.
              </p>
            )}
            {!loadingGifs && spiritGifFetchHint === 'error' && (
              <p className="spirit-gif-hint spirit-gif-hint--error" role="alert">
                Couldn&apos;t load GIFs. Try again.
              </p>
            )}
            <div className="survey-checkbox-group spirit-gif-grid" aria-busy={loadingGifs}>
            {(loadingGifs && spiritGifs.length === 0
              ? Array.from({ length: 9 }, (_, i) => ({ id: `placeholder-${i}`, placeholder: true }))
              : loadingGifs
                ? spiritGifs.map(g => ({ ...g, placeholder: true }))
                : spiritGifs
            ).map((gif) => {
              if (gif.placeholder) {
                return (
                  <div
                    key={gif.id}
                    className="survey-checkbox-option spirit-gif-option spirit-gif-placeholder"
                    aria-hidden
                  >
                    <span className="spirit-gif-spinner spirit-gif-spinner--overlay" />
                  </div>
                );
              }
              const isSelected = selectedSpiritGif?.id === gif.id;
              const previewSrc = gif.stillUrl || gif.gifUrl || gif.fullUrl || '';
              return (
                <button
                  key={gif.id}
                  className={`survey-checkbox-option spirit-gif-option ${isSelected ? 'selected' : ''}`}
                  onClick={() => {
                    const newGif = isSelected ? null : gif;
                        setSelectedSpiritGif(newGif);
                        if (newGif) {
                          handleAnswer(newGif);
                        }
                  }}
                >
                  <span className="spirit-gif-spinner spirit-gif-spinner--overlay" />
                  <img
                    src={previewSrc}
                    alt={gif.title || 'GIF'}
                    className="spirit-gif-img"
                    onLoad={(e) => {
                      const root = e.target.closest('.spirit-gif-option');
                      root?.querySelector('.spirit-gif-spinner')?.classList.add('hidden');
                      if (gif.stillUrl && gif.gifUrl && e.target.src !== gif.gifUrl) {
                        e.target.src = gif.gifUrl;
                      }
                    }}
                    onError={(e) => {
                      const img = e.target;
                      const root = img.closest('.spirit-gif-option');
                      const spinner = root?.querySelector('.spirit-gif-spinner');
                      if (gif.stillUrl && gif.gifUrl && !img.dataset.spiritTriedGif) {
                        img.dataset.spiritTriedGif = '1';
                        img.src = gif.gifUrl;
                        return;
                      }
                      spinner?.classList.add('hidden');
                      img.classList.add('spirit-gif-img--failed');
                    }}
                  />
                </button>
              );
            })}
            </div>
            <div className="brand-search-container">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!gifSearchQuery.trim() || loadingGifs) return;
                  fetchSpiritGifs(gifSearchQuery.trim());
                }}
              >
                <input
                  type="text"
                  className="brand-search-input"
                  placeholder="Search KLIPY GIFs..."
                  value={gifSearchQuery}
                  onChange={(e) => setGifSearchQuery(e.target.value)}
                  aria-label="Search KLIPY GIFs"
                />
              </form>
              <div className="spirit-gif-search-status-slot" aria-live="polite">
                {loadingGifs && (
                  <div className="brand-search-loading">Searching the web...</div>
                )}
              </div>
            </div>
          </div>
        );

      case 'user_photo':
        return (
          <div className="survey-user-photo-section">
            {surveyUserPhotoEditedUrl ? (
              <div className="survey-user-photo-confirm">
                <img
                  src={surveyUserPhotoEditedUrl}
                  alt="Your photo with fox mask"
                  className="survey-user-photo-result"
                />
                <div className="survey-user-photo-actions">
                  <button
                    type="button"
                    className="survey-user-photo-confirm-btn"
                    onClick={confirmSurveyUserPhoto}
                  >
                    USE THIS
                  </button>
                  <button
                    type="button"
                    className="survey-user-photo-retry-btn"
                    onClick={removeSurveyUserPhoto}
                  >
                    Try different photo
                  </button>
                </div>
              </div>
            ) : surveyUserPhotoPreview ? (
              <div className="survey-user-photo-preview-wrap">
                <div className="survey-user-photo-preview-container">
                  <img src={surveyUserPhotoPreview} alt="Preview" className="survey-user-photo-preview" />
                  <button
                    type="button"
                    className="survey-user-photo-remove"
                    onClick={removeSurveyUserPhoto}
                    aria-label="Remove image"
                    disabled={surveyUserPhotoUploading || surveyUserPhotoProcessing}
                  >
                    X
                  </button>
                </div>
                <button
                  type="button"
                  className="survey-user-photo-submit-btn"
                  onClick={handleSurveyUserPhotoSubmit}
                  disabled={surveyUserPhotoUploading || surveyUserPhotoProcessing}
                >
                  {surveyUserPhotoUploading || surveyUserPhotoProcessing
                    ? (
                      <span className="survey-user-photo-hint-text">
                        <img src={scissorsImage} alt="" className="survey-user-photo-scissors" />
                        {PHOTO_HINT_MESSAGES[surveyUserPhotoHintIndex]}
                      </span>
                    )
                    : 'UPLOAD'}
                </button>
              </div>
            ) : (
              <div className="survey-user-photo-upload-area">
                <div className="survey-user-photo-fox-icon">
                  <img src={foxMaskImage} alt="Fox mask" className="survey-user-photo-fox-img" />
                </div>
                <input
                  ref={surveyUserPhotoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleSurveyUserPhotoUpload}
                  id="survey-user-photo-input"
                  className="survey-user-photo-input"
                />
                <label htmlFor="survey-user-photo-input" className="survey-user-photo-upload-btn">
                  CHOOSE PHOTO
                </label>
                <p className="survey-user-photo-hint">Max 15MB · JPG, PNG, WEBP</p>
                <button
                  type="button"
                  className="survey-user-photo-skip-btn"
                  onClick={() => handleAnswer(null)}
                >
                  {collageFriendFlow ? 'My friend will upload →' : 'Skip for now →'}
                </button>
              </div>
            )}
          </div>
        );

      case 'brands':
        return (
          <div className="survey-checkbox-group">
            {question.options.map((brand, index) => {
              const isSelected = selectedBrands.some(b => b.id === brand.id);
              const isDisabled = !isSelected && selectedBrands.length >= question.maxSelections;
              return (
                <button
                  key={brand.id}
                  className={`survey-checkbox-option brand-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                  style={{ backgroundImage: `url('${brand.imageUrl}')` }}
                  onClick={() => {
                    if (isSelected) {
                      const newSelected = selectedBrands.filter(b => b.id !== brand.id);
                      setSelectedBrands(newSelected);
                    } else if (selectedBrands.length < question.maxSelections) {
                      const newSelected = [...selectedBrands, brand];
                      setSelectedBrands(newSelected);
                      if (newSelected.length === question.maxSelections) {
                        setTimeout(() => handleAnswer(newSelected), 300);
                      }
                    }
                  }}
                  disabled={isDisabled}
                  aria-label={brand.name}
                />

              );
            })}
            <button
              className="survey-checkbox-option survey-add-custom"
              onClick={() => {
                const pool = allBrands.filter(b => !selectedBrands.some(s => s.id === b.id) && !randomBrands.some(r => r.id === b.id));
                const needCount = 9 - selectedBrands.length;
                const shuffled = [...pool].sort(() => Math.random() - 0.5);
                setRandomBrands([...selectedBrands, ...shuffled.slice(0, needCount)]);
                setBrandMoreClickCount(prev => prev + 1);
              }}
            >
              MORE OPTIONS
            </button>
            {brandMoreClickCount >= 2 && !showBrandSearch && (
              <button
                className="brand-search-prompt"
                onClick={() => setShowBrandSearch(true)}
              >
                Nothing you like? Tap here to search!
              </button>
            )}
            {showBrandSearch && (
              <div className="brand-search-container">
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (!brandSearchQuery.trim() || brandSearchLoading) return;
                  setBrandSearchLoading(true);
                  try {
                    const results = await searchBrandImages(brandSearchQuery.trim());
                    setBrandSearchResults(results);
                  } catch (err) {
                    console.error('Brand search error:', err);
                    setBrandSearchResults([]);
                  } finally {
                    setBrandSearchLoading(false);
                  }
                }}>
                  <input
                    type="text"
                    className="brand-search-input"
                    placeholder="Search for any item..."
                    value={brandSearchQuery}
                    onChange={(e) => setBrandSearchQuery(e.target.value)}
                    autoFocus
                  />
                </form>
                {brandSearchLoading && <div className="brand-search-loading">Searching the web...</div>}
                {brandSearchResults.length > 0 && (
                  <div className="brand-search-results">
                    {brandSearchResults.map((result, index) => {
                      const isSelected = selectedBrands.some(b => b.id === result.id);
                      const isDisabled = !isSelected && selectedBrands.length >= question.maxSelections;
                      return (
                        <button
                          key={result.id || index}
                          className={`survey-checkbox-option brand-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                          style={{ backgroundImage: `url('${result.imageUrl}')` }}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedBrands(selectedBrands.filter(b => b.id !== result.id));
                            } else if (selectedBrands.length < question.maxSelections) {
                              const newSelected = [...selectedBrands, result];
                              setSelectedBrands(newSelected);
                              // Replace a random unselected tile in the grid with this search result
                              setRandomBrands(prev => {
                                const next = [...prev];
                                const unselectedIdx = next.findIndex(b => !newSelected.some(s => s.id === b.id));
                                if (unselectedIdx !== -1) {
                                  next[unselectedIdx] = result;
                                } else {
                                  next.push(result);
                                }
                                return next;
                              });
                              setBrandSearchResults([]);
                              setBrandSearchQuery('');
                              setShowBrandSearch(false);
                              if (newSelected.length === question.maxSelections) {
                                setTimeout(() => handleAnswer(newSelected), 300);
                              }
                            }
                          }}
                          disabled={isDisabled}
                          aria-label={result.name}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 'places':
        return (
          <div className="survey-checkbox-group">
            {question.options.map((place, index) => {
              const isSelected = selectedPlaces.some(p => p.id === place.id);
              const isDisabled = !isSelected && selectedPlaces.length >= question.maxSelections;
              return (
                <button
                  key={place.id}
                  className={`survey-checkbox-option brand-option place-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                  onClick={() => {
                    if (isSelected) {
                      const newSelected = selectedPlaces.filter(p => p.id !== place.id);
                      setSelectedPlaces(newSelected);
                    } else if (selectedPlaces.length < question.maxSelections) {
                      const newSelected = [...selectedPlaces, place];
                      setSelectedPlaces(newSelected);
                      if (newSelected.length === question.maxSelections) {
                        setTimeout(() => handleAnswer(newSelected), 300);
                      }
                    }
                  }}
                  disabled={isDisabled}
                  aria-label={place.name}
                >
                  <img src={place.imageUrl} alt={place.name} className="place-option-img" />
                </button>
              );
            })}
            <button
              className="survey-checkbox-option survey-add-custom"
              onClick={() => {
                const pool = allPlaces.filter(p => !selectedPlaces.some(s => s.id === p.id) && !randomPlaces.some(r => r.id === p.id));
                const needCount = 9 - selectedPlaces.length;
                const shuffled = [...pool].sort(() => Math.random() - 0.5);
                setRandomPlaces([...selectedPlaces, ...shuffled.slice(0, needCount)]);
                setPlaceMoreClickCount(prev => prev + 1);
              }}
            >
              MORE OPTIONS
            </button>
            {placeMoreClickCount >= 2 && !showPlaceSearch && (
              <button
                className="brand-search-prompt"
                onClick={() => setShowPlaceSearch(true)}
              >
                Nothing you like? Tap here to search!
              </button>
            )}
            {showPlaceSearch && (
              <div className="brand-search-container">
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (!placeSearchQuery.trim() || placeSearchLoading) return;
                  setPlaceSearchLoading(true);
                  setPlaceSearchDone(false);
                  try {
                    const results = await searchPlacesApi(placeSearchQuery.trim());
                    setPlaceSearchResults(results);
                  } catch (err) {
                    console.error('Place search error:', err);
                    setPlaceSearchResults([]);
                  } finally {
                    setPlaceSearchLoading(false);
                    setPlaceSearchDone(true);
                  }
                }}>
                  <input
                    type="text"
                    className="brand-search-input"
                    placeholder="Search for a place..."
                    value={placeSearchQuery}
                    onChange={(e) => setPlaceSearchQuery(e.target.value)}
                    autoFocus
                  />
                </form>
                {placeSearchLoading && <div className="brand-search-loading">Searching the web...</div>}
                {placeSearchResults.length > 0 && (
                  <div className="brand-search-results">
                    {placeSearchResults.map((result, index) => {
                      const isSelected = selectedPlaces.some(p => p.id === result.id);
                      const isDisabled = !isSelected && selectedPlaces.length >= question.maxSelections;
                      return (
                        <button
                          key={result.id || index}
                          className={`survey-checkbox-option brand-option place-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedPlaces(selectedPlaces.filter(p => p.id !== result.id));
                            } else if (selectedPlaces.length < question.maxSelections) {
                              const newSelected = [...selectedPlaces, result];
                              setSelectedPlaces(newSelected);
                              setRandomPlaces(prev => {
                                const next = [...prev];
                                const unselectedIdx = next.findIndex(p => !newSelected.some(s => s.id === p.id));
                                if (unselectedIdx !== -1) {
                                  next[unselectedIdx] = result;
                                } else {
                                  next.push(result);
                                }
                                return next;
                              });
                              setPlaceSearchResults([]);
                              setPlaceSearchQuery('');
                              setShowPlaceSearch(false);
                              if (newSelected.length === question.maxSelections) {
                                setTimeout(() => handleAnswer(newSelected), 300);
                              }
                            }
                          }}
                          disabled={isDisabled}
                          aria-label={result.name}
                        >
                          <img src={result.imageUrl} alt={result.name} className="place-option-img" />
                        </button>
                      );
                    })}
                  </div>
                )}
                {placeSearchDone && !placeSearchLoading && placeSearchResults.length === 0 && (
                  <div className="brand-search-loading">No places found</div>
                )}
              </div>
            )}
          </div>
        );

      case 'multiple':
        let currentSelected;
        if (question.id === 'music') {
          currentSelected = selectedMusic;
        } else if (question.id === 'movies') {
          currentSelected = selectedMovies;
        } else if (question.id === 'activities') {
          currentSelected = selectedActivities;
        } else {
          currentSelected = [];
        }
        const isShowingCustomInput = showCustomInput[question.id];
        const isAddingNew = isShowingCustomInput && !editingCustomValue;
        // Filter out predefined options to show only custom values
        const predefinedOptions = question.options;
        const customValues = currentSelected.filter(item => !predefinedOptions.includes(item));
        
        return (
          <div className="survey-checkbox-group">
            {question.options.map((option, index) => (
              <button
                key={index}
                className={`survey-checkbox-option ${
                  currentSelected.includes(option) ? 'selected' : ''
                } ${question.id === 'music' ? 'music-option' : ''} ${question.id === 'movies' ? 'movie-option' : ''} ${question.id === 'activities' ? 'activities-option' : ''}`}
                onClick={() => handleMultipleChoice(option)}
                disabled={question.maxSelections && currentSelected.length >= question.maxSelections && !currentSelected.includes(option)}
              >
                {(question.id === 'music' || question.id === 'movies') ? (
                  <span className="tile-title-plaque">{option}</span>
                ) : (
                  option
                )}
              </button>
            ))}
            {customValues.map((customValue, index) => {
              // If this custom value is being edited, show input field instead
              if (editingCustomValue === customValue) {
                return (
                  <input
                    key={`custom-edit-${index}`}
                    type="text"
                    className="survey-checkbox-option survey-custom-input"
                    placeholder="Please specify"
                    value={customInputValue}
                    onChange={(e) => setCustomInputValue(e.target.value)}
                    onKeyPress={handleCustomInputSubmit}
                    onBlur={handleCustomInputBlur}
                    autoFocus
                  />
                );
              }
              // Otherwise show the custom value as a button
              return (
                <div
                  key={`custom-${index}`}
                  className="custom-tile-wrapper"
                  style={{ position: 'relative' }}
                >
                  <button
                    className={`survey-checkbox-option selected ${question.id === 'music' ? 'music-option' : ''} ${question.id === 'movies' ? 'movie-option' : ''} ${question.id === 'activities' ? 'activities-option' : ''}`}
                    onClick={() => handleMultipleChoice(customValue)}
                  >
                    {(question.id === 'music' || question.id === 'movies') ? (
                      <span className="tile-title-plaque">{customValue}</span>
                    ) : (
                      customValue
                    )}
                  </button>
                  <button
                    className="custom-tile-delete"
                    onClick={(e) => handleDeleteCustomValue(customValue, e)}
                    aria-label="Delete"
                  >
                    ×
                  </button>
                </div>
              );
            })}
            {isAddingNew && question.id !== 'activities' ? (
              <input
                type="text"
                className="survey-checkbox-option survey-custom-input"
                placeholder="Please specify"
                value={customInputValue}
                onChange={(e) => setCustomInputValue(e.target.value)}
                onKeyPress={handleCustomInputSubmit}
                onBlur={handleCustomInputBlur}
                autoFocus
              />
            ) : question.id === 'activities' && showEmojiDropdown ? (
              <div className="emoji-dropdown-container">
                <div className="emoji-dropdown">
                  {allActivityEmojis
                    .filter(emoji => !question.options.includes(emoji))
                    .map((emoji, index) => (
                      <button
                        key={index}
                        className="emoji-dropdown-item"
                        onClick={() => {
                          const newSelected = [...selectedActivities, emoji];
                          setSelectedActivities(newSelected);
                          setShowEmojiDropdown(false);
                          // Auto-advance if max selections reached
                          if (question.maxSelections && newSelected.length === question.maxSelections) {
                            setTimeout(() => {
                              handleAnswer(newSelected);
                            }, 100);
                          }
                        }}
                        disabled={question.maxSelections && selectedActivities.length >= question.maxSelections}
                      >
                        {emoji}
                      </button>
                    ))}
                </div>
                <button
                  className="emoji-dropdown-close"
                  onClick={() => setShowEmojiDropdown(false)}
                >
                  X
                </button>
              </div>
            ) : (question.id === 'music' || question.id === 'movies') ? (
              <button
                className="survey-checkbox-option survey-add-custom"
                onClick={() => handleMultipleChoice('show new options')}
              >
                MORE OPTIONS
              </button>
            ) : question.id === 'activities' ? (
              <>
                <button
                  type="button"
                  className="survey-checkbox-option survey-add-custom"
                  onClick={() => {
                    const pool = allActivityEmojis.filter(
                      (e) =>
                        !religionEmojis.includes(e) &&
                        !selectedActivities.includes(e) &&
                        !activityEmojis.includes(e)
                    );
                    const needCount = 9 - selectedActivities.length;
                    const shuffled = [...pool].sort(() => Math.random() - 0.5);
                    setActivityEmojis([...selectedActivities, ...shuffled.slice(0, needCount)]);
                    setActivityEmojiMoreClickCount((c) => c + 1);
                  }}
                >
                  MORE OPTIONS
                </button>
                {activityEmojiMoreClickCount >= 2 && !showActivityEmojiSearch && (
                  <button
                    type="button"
                    className="brand-search-prompt"
                    onClick={() => {
                      setShowActivityEmojiSearch(true);
                      setActivityEmojiSearchResults([]);
                      setActivityEmojiSearchDone(false);
                    }}
                  >
                    Nothing you like? Tap here to search!
                  </button>
                )}
                {showActivityEmojiSearch && (
                  <div className="brand-search-container">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const q = activityEmojiSearchQuery.trim();
                        if (!q) return;
                        const results = searchActivityPlusEmojisByQuery(q, 9);
                        setActivityEmojiSearchResults(results);
                        setActivityEmojiSearchDone(true);
                      }}
                    >
                      <input
                        type="text"
                        className="brand-search-input"
                        placeholder="Search emojis..."
                        value={activityEmojiSearchQuery}
                        onChange={(e) => {
                          setActivityEmojiSearchQuery(e.target.value);
                          setActivityEmojiSearchResults([]);
                          setActivityEmojiSearchDone(false);
                        }}
                        autoFocus
                        aria-label="Search emojis by keyword"
                      />
                    </form>
                    {activityEmojiSearchResults.length > 0 && (
                      <div className="brand-search-results">
                        {activityEmojiSearchResults.map((emoji, index) => {
                          const isSelected = selectedActivities.includes(emoji);
                          const isDisabled =
                            !isSelected && selectedActivities.length >= question.maxSelections;
                          return (
                            <button
                              type="button"
                              key={`emoji-search-${emoji}-${index}`}
                              className={`survey-checkbox-option activities-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                              onClick={() => {
                                if (isSelected) {
                                  setSelectedActivities(selectedActivities.filter((e) => e !== emoji));
                                } else if (selectedActivities.length < question.maxSelections) {
                                  const newSelected = [...selectedActivities, emoji];
                                  setSelectedActivities(newSelected);
                                  setActivityEmojis((prev) => {
                                    const next = [...prev];
                                    const unselectedIdx = next.findIndex((e) => !newSelected.includes(e));
                                    if (unselectedIdx !== -1) next[unselectedIdx] = emoji;
                                    else if (!next.includes(emoji)) next.push(emoji);
                                    return next.slice(0, 9);
                                  });
                                  setActivityEmojiSearchResults([]);
                                  setActivityEmojiSearchQuery('');
                                  setShowActivityEmojiSearch(false);
                                  setActivityEmojiSearchDone(false);
                                  if (newSelected.length === question.maxSelections) {
                                    setTimeout(() => handleAnswer(newSelected), 300);
                                  }
                                }
                              }}
                              disabled={isDisabled}
                              aria-label={emoji}
                            >
                              {emoji}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {activityEmojiSearchDone && activityEmojiSearchResults.length === 0 && (
                      <div className="brand-search-loading">No emojis matched</div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <button
                className="survey-checkbox-option survey-add-custom"
                onClick={() => handleMultipleChoice('+ add yours')}
              >
                MORE OPTIONS
              </button>
            )}
          </div>
        );

      default:
        return (
          <div className="survey-options">
            {question.options.map((option, index) => (
              <button
                key={index}
                className={`survey-option ${(answers[question.id] === option || (question.id === 'class_year' && option === 'Other' && showOtherInputFor === 'class_year')) ? 'selected' : ''}`}
                onClick={() => {
                  if (question.id === 'class_year' && option === 'Other') {
                    setShowOtherInputFor('class_year');
                    setOtherInputValue('');
                  } else {
                    handleAnswer(option);
                  }
                }}
              >
                {option}
              </button>
            ))}
            {question.id === 'class_year' && showOtherInputFor === 'class_year' && (
              <div className="survey-other-input-wrap" style={{ width: '200px', marginTop: '0.5rem', marginLeft: 'auto', marginRight: 'auto' }}>
                <div className="survey-other-input-with-submit">
                  <input
                    type="text"
                    className="survey-checkbox-option survey-custom-input survey-other-input"
                    style={{ width: '100%', boxSizing: 'border-box' }}
                    placeholder="Please specify"
                    value={otherInputValue}
                    onChange={(e) => setOtherInputValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && otherInputValue.trim() && !isSubmittingOther) {
                        setIsSubmittingOther(true);
                        setTimeout(() => {
                          handleAnswer(otherInputValue.trim());
                          setShowOtherInputFor(null);
                          setOtherInputValue('');
                          setIsSubmittingOther(false);
                        }, 300);
                      }
                    }}
                    autoFocus
                  />
                  <button
                    type="button"
                    className="survey-other-submit-btn"
                    onClick={() => {
                      if (otherInputValue.trim()) {
                        setIsSubmittingOther(true);
                        // Show checkmark briefly, then submit
                        setTimeout(() => {
                          handleAnswer(otherInputValue.trim());
                          setShowOtherInputFor(null);
                          setOtherInputValue('');
                          setIsSubmittingOther(false);
                        }, 300);
                      }
                    }}
                    disabled={!otherInputValue.trim() || isSubmittingOther}
                    aria-label="Submit"
                  >
                    {isSubmittingOther ? '✓' : '→'}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div className="inline-survey-content">
      {showIntro ? (
        <div className="survey-intro-page">
          <button className="survey-back survey-back--main" onClick={onClose} aria-label="Close survey">‹</button>
          <div className="survey-intro-hearts">
            <div className="survey-intro-heart-item">
              <div className="survey-intro-heart">
                <img src={heartOutline} alt="" className="survey-intro-heart-svg" />
                <span>1</span>
              </div>
              <p>Fill your collage with visuals that capture your vibe.</p>
            </div>
            <div className="survey-intro-heart-item">
              <div className="survey-intro-heart">
                <img src={heartOutline} alt="" className="survey-intro-heart-svg" />
                <span>2</span>
              </div>
              <p>Add your photo and we&apos;ll mask it for privacy.</p>
            </div>
            <div className="survey-intro-heart-item">
              <div className="survey-intro-heart">
                <img src={heartOutline} alt="" className="survey-intro-heart-svg" />
                <span>3</span>
              </div>
              <p>Get intros based on your collage vibe.</p>
            </div>
          </div>
          <button className="survey-intro-start-btn" onClick={() => setShowIntro(false)}>Start</button>
        </div>
      ) : (
      <>
      {!showThankYou && !showCutouts && !showSwagSelection && !showWordsSelection && !showActivitiesSelection && !showActivitiesTiles && (
        <button 
          className="survey-back survey-back--main"
          onClick={handleBack}
          disabled={isBackDisabled()}
          aria-label="Go back to previous question"
        >
          ‹
        </button>
      )}
      {!showThankYou && !showCutouts && !showSwagSelection && !showWordsSelection && !showActivitiesSelection && !showActivitiesTiles && navigatedBack && isFullyAnswered() && (
        <button 
          className="survey-next survey-next--main"
          onClick={handleNext}
          aria-label="Go to next question"
        >
          ›
        </button>
      )}
      <p className="survey-fill-hint">{visualsHintText || DEFAULT_VISUALS_HINT}</p>
      <div className="survey-progress">
        <div
          className="survey-progress-bar"
          style={{ width: showThankYou ? '100%' : `${((currentQuestion + 1) / questions.length) * 100}%` }}
        />
      </div>

      {!showThankYou && !showCutouts && !showSwagSelection && !showWordsSelection && !showActivitiesSelection && !showActivitiesTiles ? (
        <>
          <h2 className="survey-question">
            {questions[currentQuestion].question}
          </h2>
          {questions[currentQuestion].subtitle && (
            <p className={`survey-subtitle ${['music', 'movies', 'vibe_photo'].includes(questions[currentQuestion].id) ? 'survey-subtitle-music' : ''}`}>
              {questions[currentQuestion].subtitle}
            </p>
          )}
          {renderQuestionContent()}
        </>
      ) : showCutouts ? (
        <div className="cutout-selection">
          <h2 className="survey-question">Pick their look</h2>
          {loadingCutouts ? (
            <div className="cutout-loading">Loading options...</div>
          ) : cutouts.length > 0 ? (
            <>
              <div className="cutout-grid">
                {cutouts.map((cutout) => (
                  <button
                    key={cutout.id}
                    className={`cutout-option ${selectedCutout?.id === cutout.id ? 'selected' : ''}`}
                    onClick={() => setSelectedCutout(cutout)}
                  >
                    {cutout.imageUrl ? (
                      <img 
                        src={cutout.imageUrl} 
                        alt={cutout.name || 'Partner option'} 
                        className="cutout-image"
                      />
                    ) : (
                      <div className="cutout-placeholder">
                        <span>👤</span>
                        <p>{cutout.name || 'Partner'}</p>
                      </div>
                    )}
                  </button>
                ))}
              </div>
              <button
                className="cutout-confirm-button"
                onClick={() => {
                  // Handle cutout selection
                  if (selectedCutout) {
                    console.log('Selected cutout:', selectedCutout);
                    // Load swags and show swag selection
                    loadSwags();
                    setSelectedSwags([]); // Reset swag selection
                    setShowCutouts(false);
                    setShowSwagSelection(true);
                  }
                }}
                disabled={!selectedCutout}
              >
                {selectedCutout ? 'Continue' : 'Select a look'}
              </button>
            </>
          ) : (
            <div className="cutout-error">
              <p>No partner options available at the moment.</p>
              <button onClick={onClose} className="cutout-confirm-button">
                Close
              </button>
            </div>
          )}
        </div>
      ) : showSwagSelection ? (
        <div className="swag-selection">
          <button 
            className="survey-back"
            onClick={() => {
              setShowSwagSelection(false);
              setShowCutouts(true);
              setSelectedSwags([]); // Reset swag selection when going back
            }}
            aria-label="Go back to look selection"
          >
            ‹
          </button>
          <h2 className="survey-question">Pick their things</h2>
          <p className="survey-subtitle">Select 3 items that represent their vibe</p>
          {swags.length > 0 ? (
            <>
              <div className="swag-grid">
                {swags.map((swag) => {
                  const isSelected = selectedSwags.some(s => s.id === swag.id);
                  const isDisabled = !isSelected && selectedSwags.length >= 3;
                  return (
                    <button
                      key={swag.id}
                      className={`swag-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                      onClick={() => {
                        if (isSelected) {
                          // Deselect if already selected
                          setSelectedSwags(selectedSwags.filter(s => s.id !== swag.id));
                        } else if (selectedSwags.length < 3) {
                          // Select if under limit
                          setSelectedSwags([...selectedSwags, swag]);
                        }
                      }}
                      disabled={isDisabled}
                    >
                      {swag.imageUrl ? (
                        <img 
                          src={swag.imageUrl} 
                          alt={swag.name || 'Swag option'} 
                          className="swag-image"
                        />
                      ) : (
                        <div className="swag-placeholder">
                          <span>🎁</span>
                          <p>{swag.name || 'Swag'}</p>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              <button
                className="cutout-confirm-button"
                onClick={() => {
                  // Handle swag selection
                  if (selectedSwags.length > 0) {
                    console.log('Selected swags:', selectedSwags);
                    console.log('Selected cutout:', selectedCutout);
                    // Reset words selection and show words selection
                    setSelectedWords([]);
                    setShowSwagSelection(false);
                    setShowWordsSelection(true);
                  }
                }}
                disabled={selectedSwags.length === 0}
              >
                {selectedSwags.length > 0 
                  ? `Continue${selectedSwags.length > 1 ? ` (${selectedSwags.length} selected)` : ''}` 
                  : 'Select swag'}
              </button>
            </>
          ) : (
            <div className="cutout-error">
              <p>No swag options available at the moment.</p>
              <button onClick={onClose} className="cutout-confirm-button">
                Close
              </button>
            </div>
          )}
        </div>
      ) : showWordsSelection ? (
        <div className="words-selection">
          <button 
            className="survey-back"
            onClick={() => {
              setShowWordsSelection(false);
              setShowSwagSelection(true);
              setSelectedWords([]); // Reset words selection when going back
            }}
            aria-label="Go back to things selection"
          >
            ‹
          </button>
          <h2 className="survey-question">Pick their words</h2>
          <p className="survey-subtitle">Select 3 phrases that represent their vibe</p>
          <div className="words-grid">
            {availableQuotes.map((quote, index) => {
              const isSelected = selectedWords.includes(quote);
              const isDisabled = !isSelected && selectedWords.length >= 3;
              return (
                <button
                  key={index}
                  className={`word-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                  onClick={() => {
                    if (isSelected) {
                      // Deselect if already selected
                      setSelectedWords(selectedWords.filter(w => w !== quote));
                    } else if (selectedWords.length < 3) {
                      // Select if under limit
                      setSelectedWords([...selectedWords, quote]);
                    }
                  }}
                  disabled={isDisabled}
                >
                  {quote.toLowerCase()}
                </button>
              );
            })}
          </div>
          <button
            className="cutout-confirm-button"
            onClick={() => {
              // Handle words selection
              if (selectedWords.length > 0) {
                console.log('Selected words:', selectedWords);
                console.log('Selected swags:', selectedSwags);
                console.log('Selected cutout:', selectedCutout);
                // Pass selected data to parent component and complete
                if (onComplete && selectedCutout) {
                  onComplete({
                    answers: answers,
                    cutout: selectedCutout,
                    swags: selectedSwags,
                    words: selectedWords,
                    activities: [],
                    places: answers.places || [],
                    generatedCutoutUrl: generatedCutoutUrlRef.current || generatedCutoutUrl || undefined,
                    jungianAnalysis: jungianAnalysisRef.current || undefined,
                    analysisPromise: analysisPromiseRef.current || undefined,
                    generationPromise: generationPromiseRef.current || undefined,
                    descriptorPromise: descriptorPromiseRef.current || undefined,
                  });
                }
                onClose(selectedCutout ? { completed: true } : undefined);
              }
            }}
            disabled={selectedWords.length === 0}
          >
            {selectedWords.length > 0 
              ? `Continue${selectedWords.length > 1 ? ` (${selectedWords.length} selected)` : ''}` 
              : 'Select phrases'}
          </button>
        </div>
      ) : showActivitiesTiles ? (
        <div className="cutout-selection">
          {(() => {
            // Check if we're in the questions flow (currentQuestion points to activities)
            const isInQuestionsFlow = questions[currentQuestion]?.id === 'activities';
            const moviesQuestionIndex = questions.findIndex(q => q.id === 'movies');
            
            return (
              <>
                <button 
                  className="survey-back"
                  onClick={() => {
                    if (isInQuestionsFlow) {
                      // In questions flow: go back to movies question
                      setShowActivitiesTiles(false);
                      setCurrentQuestion(moviesQuestionIndex);
                      setSelectedActivities([]); // Reset activities selection when going back
                    } else {
                      // After words flow: go back to words selection
                      setShowActivitiesTiles(false);
                      setShowWordsSelection(true);
                      setSelectedActivities([]); // Reset activities selection when going back
                    }
                  }}
                  aria-label={isInQuestionsFlow ? "Go back to movies question" : "Go back to words selection"}
                >
                  ‹
                </button>
                <h2 className="survey-question">Choose how you spend time together</h2>
                <p className="survey-subtitle">Select up to 3 activities</p>
                {loadingActivityImages && fetchedActivities.length === 0 ? (
                  <div className="cutout-loading">Loading activity images...</div>
                ) : fetchedActivities.length > 0 ? (
                  <>
                    <div className="activities-tiles-grid">
                      {fetchedActivities.map((activity) => {
                        const isSelected = selectedActivities.some(a => a.id === activity.id);
                        const isDisabled = !isSelected && selectedActivities.length >= 3;
                        return (
                          <button
                            key={activity.id}
                            className={`activity-tile ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedActivities(selectedActivities.filter(a => a.id !== activity.id));
                              } else if (selectedActivities.length < 3) {
                                setSelectedActivities([...selectedActivities, activity]);
                              }
                            }}
                            disabled={isDisabled}
                          >
                            {activity.imageUrl ? (
                              <img 
                                src={activity.imageUrl} 
                                alt={activity.name} 
                                className="activity-tile-image"
                              />
                            ) : (
                              <div className="activity-tile-placeholder">
                                <span>{activity.name}</span>
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      className="cutout-confirm-button"
                      onClick={async () => {
                        // Handle activities selection
                        if (selectedActivities.length > 0) {
                          if (isInQuestionsFlow) {
                            // In questions flow: save answer and go to cutouts
                            await handleAnswer(selectedActivities);
                          } else {
                            // After words flow: complete survey
                            console.log('Selected activities:', selectedActivities);
                            console.log('Selected words:', selectedWords);
                            console.log('Selected swags:', selectedSwags);
                            console.log('Selected cutout:', selectedCutout);
                            // Pass selected data to parent component
                            if (onComplete && selectedCutout) {
                              onComplete({
                                answers: answers,
                                cutout: selectedCutout,
                                swags: selectedSwags,
                                words: selectedWords,
                                activities: selectedActivities,
                                places: answers.places || [],
                                generatedCutoutUrl: generatedCutoutUrlRef.current || generatedCutoutUrl || undefined,
                                jungianAnalysis: jungianAnalysisRef.current || undefined,
                                analysisPromise: analysisPromiseRef.current || undefined,
                                generationPromise: generationPromiseRef.current || undefined,
                                descriptorPromise: descriptorPromiseRef.current || undefined,
                              });
                            }
                            onClose(selectedCutout ? { completed: true } : undefined);
                          }
                        }
                      }}
                      disabled={selectedActivities.length === 0}
                    >
                      {selectedActivities.length > 0 
                        ? `Continue${selectedActivities.length > 1 ? ` (${selectedActivities.length} selected)` : ''}` 
                        : 'Select activity'}
                    </button>
                  </>
                ) : (
                  <div className="cutout-error">
                    <p>No activity options available at the moment.</p>
                    <button onClick={onClose} className="cutout-confirm-button">
                      Close
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      ) : showActivitiesSelection ? (
        <div className="activities-selection">
          <button 
            className="survey-back"
            onClick={() => {
              setShowActivitiesSelection(false);
              setShowSwagSelection(true);
              setSelectedPartnerActivities([]); // Reset activities selection when going back
            }}
            aria-label="Go back to swag selection"
          >
            ‹
          </button>
          <h2 className="survey-question">What do they like to do?</h2>
          <p className="survey-subtitle">Select up to 3 activities</p>
          {partnerActivities.length > 0 ? (
            <>
              <div className="activities-grid">
                {partnerActivities.map((activity) => {
                  const isSelected = selectedPartnerActivities.some(a => a.id === activity.id);
                  const isDisabled = !isSelected && selectedPartnerActivities.length >= 3;
                  return (
                    <button
                      key={activity.id}
                      className={`activity-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                      onClick={() => {
                        if (isSelected) {
                          // Deselect if already selected
                          setSelectedPartnerActivities(selectedPartnerActivities.filter(a => a.id !== activity.id));
                        } else if (selectedPartnerActivities.length < 3) {
                          // Select if under limit
                          setSelectedPartnerActivities([...selectedPartnerActivities, activity]);
                        }
                      }}
                      disabled={isDisabled}
                    >
                      {activity.imageUrl ? (
                        <img 
                          src={activity.imageUrl} 
                          alt={activity.name || 'Activity option'} 
                          className="activity-image"
                        />
                      ) : (
                        <div className="activity-placeholder">
                          <span>🎯</span>
                          <p>{activity.name || 'Activity'}</p>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              <button
                className="cutout-confirm-button"
                onClick={() => {
                  // Handle activities selection
                  if (selectedPartnerActivities.length > 0) {
                    console.log('Selected activities:', selectedPartnerActivities);
                    console.log('Selected swags:', selectedSwags);
                    console.log('Selected cutout:', selectedCutout);
                    // Pass selected cutout image to parent component
                    if (onComplete && selectedCutout) {
                      onComplete({
                        answers: answers,
                        cutout: selectedCutout,
                        swags: selectedSwags,
                        activities: selectedPartnerActivities,
                        places: answers.places || [],
                        generatedCutoutUrl: generatedCutoutUrlRef.current || generatedCutoutUrl || undefined,
                        jungianAnalysis: jungianAnalysisRef.current || undefined,
                        analysisPromise: analysisPromiseRef.current || undefined,
                        generationPromise: generationPromiseRef.current || undefined,
                        descriptorPromise: descriptorPromiseRef.current || undefined,
                      });
                    }
                  }
                  onClose(selectedCutout ? { completed: true } : undefined);
                }}
                disabled={selectedPartnerActivities.length === 0}
              >
                {selectedPartnerActivities.length > 0 
                  ? `Continue${selectedPartnerActivities.length > 1 ? ` (${selectedPartnerActivities.length} selected)` : ''}` 
                  : 'Select activity'}
              </button>
            </>
          ) : (
            <div className="cutout-error">
              <p>No activity options available at the moment.</p>
              <button onClick={onClose} className="cutout-confirm-button">
                Close
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="survey-thank-you">
          <h2 className="survey-question">You are on the waitlist! We'll be in touch soon.</h2>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default InlineSurvey;
